import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaSignOutAlt, FaUserCircle, FaArrowUp, FaArrowDown, FaHome, FaBookOpen } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';

const PageLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const { navigateTo } = useNavigation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showUpButton, setShowUpButton] = useState(false);

    // --- SCROLL LISTENER ---
    useEffect(() => {
        const checkScroll = () => {
            if (!window) return;
            // Show UP button if we are scrolled down a bit
            if (window.scrollY > 300) {
                setShowUpButton(true);
            } else {
                setShowUpButton(false);
            }
        };

        window.addEventListener('scroll', checkScroll);
        return () => window.removeEventListener('scroll', checkScroll);
    }, []);

    // --- GRADUAL SEGMENT SCROLLING ---
    // Scrolls by 70% of the viewport height (gentle segment)
    const scrollUp = () => {
        window.scrollBy({ 
            top: -window.innerHeight * 0.7, 
            behavior: 'smooth' 
        });
    };

    const scrollDown = () => {
        window.scrollBy({ 
            top: window.innerHeight * 0.7, 
            behavior: 'smooth' 
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative font-sans">
            
            {/* BACKGROUND BLOBS */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
            </div>

            {/* HEADER */}
            <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-sm z-50 h-20 flex items-center justify-between px-6 border-b border-gray-200">
                <div onClick={() => navigateTo('dashboard')} className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition">
                    <img src="/logo.png" alt="BP" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="text-2xl font-black text-gray-800 tracking-tight">BouncePen</span>
                </div>

                <div className="hidden md:flex items-center gap-6">
                    <button onClick={() => navigateTo('dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-bold transition-colors">
                        <FaHome size={20} /> Dashboard
                    </button>

                    <button onClick={() => navigateTo('eslCourses')} className="flex items-center gap-2 text-gray-600 hover:text-teal-600 font-bold transition-colors">
                        <FaBookOpen size={20} /> Lessons
                    </button>

                    {user && (
                        <div className="flex items-center gap-3 pl-6 border-l border-gray-300">
                            <div className="flex items-center gap-2 text-right">
                                <div>
                                    <p className="text-sm font-bold text-gray-800 leading-none">{user.name}</p>
                                    <p className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">{user.role}</p>
                                </div>
                                <FaUserCircle className="text-gray-400 text-3xl" />
                            </div>
                            <button onClick={logout} className="bg-red-50 text-red-500 p-2 rounded-full hover:bg-red-500 hover:text-white transition-all"><FaSignOutAlt /></button>
                        </div>
                    )}
                </div>

                <button className="md:hidden text-2xl text-gray-700 p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    {isMenuOpen ? <FaTimes /> : <FaBars />}
                </button>
            </header>

            {/* MOBILE DROPDOWN */}
            {isMenuOpen && (
                <div className="fixed top-20 left-0 right-0 bg-white border-b shadow-xl z-40 md:hidden animate-fade-in">
                    <div className="p-4 space-y-2">
                        <button onClick={() => { navigateTo('dashboard'); setIsMenuOpen(false); }} className="w-full text-left p-4 font-bold text-gray-700 bg-gray-50 rounded-xl flex items-center gap-3"><FaHome /> Dashboard</button>
                        <button onClick={() => { navigateTo('eslCourses'); setIsMenuOpen(false); }} className="w-full text-left p-4 font-bold text-teal-700 bg-teal-50 rounded-xl flex items-center gap-3"><FaBookOpen /> Lessons</button>
                        <button onClick={logout} className="w-full text-left p-4 font-bold text-red-500 hover:bg-red-50 rounded-xl flex items-center gap-3"><FaSignOutAlt /> Log Out</button>
                    </div>
                </div>
            )}

            <main className="relative z-10 flex-1 pt-32 px-4 pb-20 max-w-7xl mx-auto w-full">
                {children}
            </main>

            {/* SCROLL BUTTONS - Z-INDEX INCREASED TO 100 TO SIT ABOVE LESSON FOOTERS */}
            <div className="fixed bottom-24 right-8 z-[100] flex flex-col gap-3">
                {showUpButton && (
                    <button 
                        onClick={scrollUp} 
                        className="bg-blue-600 text-white w-12 h-12 rounded-full shadow-xl flex items-center justify-center text-xl hover:bg-blue-700 hover:-translate-y-1 transition-all active:scale-95 border-2 border-white opacity-90 hover:opacity-100"
                        title="Scroll Up"
                    >
                        <FaArrowUp />
                    </button>
                )}
                <button 
                    onClick={scrollDown} 
                    className="bg-gray-700 text-white w-12 h-12 rounded-full shadow-xl flex items-center justify-center text-xl hover:bg-gray-900 hover:translate-y-1 transition-all active:scale-95 border-2 border-white opacity-90 hover:opacity-100"
                    title="Scroll Down"
                >
                    <FaArrowDown />
                </button>
            </div>
        </div>
    );
};

export default PageLayout;